import { DialogDisclosure, Heading, HeadingLevel } from "@ariakit/react";
import { useMediaQuery } from "@indietabletop/appkit/use-media-query";
import { useRef } from "react";
import { Link, Route, Switch, useLocation, useParams } from "wouter";
import { Army } from "../../domain/army";
import { Unit } from "../../domain/unit";
import {
  ActionsMenu,
  MenuDialogDisclosure,
  MenuDivider,
  MenuLink,
} from "../actions-menu";
import { UpdateArmyDialog } from "../army-dialog";
import { ArmyKeywordsDialog } from "../army-keywords-dialog";
import { atomic } from "../atomic.css";
import { ConfirmDialog } from "../confirm-dialog";
import {
  ArmyConstraintsValidationBox,
  InfoBox,
} from "../constraints-validation";
import { ContentView } from "../content-view";
import { useDatabase } from "../database-provider";
import { DialogDisclosureButton } from "../dialog-buttons";
import { DialogTrigger } from "../dialog-trigger";
import { KnownErrorView } from "../error-view";
import { useArmyQuery } from "../hooks/use-army-query";
import { useFetchSharedArmy } from "../hooks/use-fetch-shared-army";
import { useRulesetChangelogHref } from "../hooks/use-ruleset-changelog";
import { Icon } from "../icon";
import { IconButton } from "../icon-button";
import { KeywordItem } from "../keyword-item";
import { DialogListButton } from "../list-button";
import { ListEndMarker } from "../list-end-marker";
import { LoadingView } from "../loading-view";
import { MinWidth } from "../media";
import { Multiplier } from "../multiplier-counter";
import { Paragraphs } from "../paragraphs";
import { useRulesets } from "../ruleset-provider/context";
import { ShareArmyDialog } from "../share-army-dialog";
import { SubtleHeading } from "../subtle-heading";
import { useDocumentTheme } from "../theme/use-document-theme";
import { CreateUnitDialog } from "../unit-dialog";
import { UnitView } from "../unit-view";
import { VerticalDivider } from "../vertical-divider";
import * as css from "./style.css";

function UnitListItem(props: { unit: Unit }) {
  const { unit } = props;
  const [location] = useLocation();

  const href = `/u/${unit.data.id}`;
  const isActive = href === location;

  return (
    <li key={unit.data.id} className={css.unitListItem}>
      <Link
        href={href}
        className={atomic({
          paddingBlockStart: 18,
          paddingBlockEnd: 16,
          paddingInlineStart: isActive ? 16 : { hover: 16, default: 0 },
          borderInlineStart: isActive ? "thick" : "none",
          display: "grid",
          gridTemplateColumns: "fraction-auto",
          gap: 12,
          alignItems: "center",
          transition: "all",
          backgroundColor: {
            hover: "background-secondary",
          },
        })}
      >
        <div>
          <div className={css.unitName}>
            {unit.name}
            {unit.data.count > 1 && (
              <>
                {" "}
                <Multiplier count={unit.data.count} />
              </>
            )}
          </div>

          <div
            className={css.unitSummary}
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {!unit.isValid && (
              <span
                className={atomic({
                  backgroundColor: "text",
                  color: "background-primary",
                  fontFamily: "arboria",
                  fontWeight: "black",
                  fontStyle: "italic",
                  textTransform: "uppercase",
                  paddingInline: 6,
                  fontSize: 12,
                  paddingBlockEnd: 2,
                  marginInlineEnd: 6,
                })}
              >
                Invalid
              </span>
            )}
            {unit.summary}
          </div>
        </div>

        <Icon name="forward" />
      </Link>
    </li>
  );
}

function UnitDetailView(props: {
  army: Army;
  headerIcon: "back" | "cross";
  readOnly?: boolean;
}) {
  const params = useParams<{ unitId: string }>();
  const unit = props.army.getUnit(params.unitId);

  if (!unit) {
    return (
      <HeadingLevel>
        <ContentView
          heading={!props.readOnly ? "Unit" : "Shared Unit"}
          backHref="/"
          icon={props.headerIcon}
        >
          <Heading
            className={atomic({
              fontFamily: "barlow-condensed",
              fontSize: 24,
              fontWeight: "semi-bold",
              marginBlockEnd: 8,
            })}
          >
            Unit not found
          </Heading>
          <p
            className={atomic({
              maxInlineSize: "optimal-reading",
              lineHeight: 1.5,
              fontSize: 16,
            })}
          >
            The link you followed might be broken, or the unit you were looking
            for was removed.
          </p>
        </ContentView>
      </HeadingLevel>
    );
  }

  return (
    <HeadingLevel>
      <UnitView {...props} unit={unit} />
    </HeadingLevel>
  );
}

/**
 * Makes a dialog disclosure button render like an inline text link.
 */
const inlineDisclosure = atomic({
  display: "inline",
  inlineSize: "fit-content",
  fontSize: "inherit",
  fontFamily: "inherit",
  fontWeight: "inherit",
  textDecoration: "underline",
});

function UpdateRulesetDialog(props: { army: Army }) {
  const { army } = props;
  const db = useDatabase();
  const rulesets = useRulesets();
  const changelogHref = useRulesetChangelogHref(rulesets.latest.version);

  return (
    <ConfirmDialog
      heading="Update ruleset?"
      description={
        <>
          {army.name} will be updated to ruleset v{rulesets.latest.version}.
          {changelogHref && (
            <Link
              href={changelogHref}
              className={atomic({ textDecoration: "underline" })}
            >
              See what changed
            </Link>
          )}
        </>
      }
      submitLabel="Update"
      onSubmit={async () => {
        await db.updateArmy({
          ...army.data,
          ruleset_version: rulesets.latest.version,
        });
      }}
    />
  );
}

function ArmyDetailView(props: { army: Army; readOnly?: boolean }) {
  const { army, readOnly } = props;

  const editButtonRef = useRef<HTMLDivElement | null>(null);
  const db = useDatabase();
  const rulesets = useRulesets();
  const [_, navigate] = useLocation();

  // Offer to bump the army to the latest ruleset only across a major version.
  // Minor and patch updates are picked up automatically, see
  // RulesetRegistry.resolve.
  const canUpdateRuleset = !readOnly && rulesets.isOutdated(army.ruleset);

  // In readOnly mode, we want to show the army keyword section only if the
  // keyword is present. In readWrite mode, we show it unconditionally, because
  // it allows the user to add the keyword.
  const showArmyKeywordSection = (readOnly && !!army.keyword) || !readOnly;

  return (
    <>
      <ContentView
        heading={!readOnly ? "Army" : "Shared Army"}
        backHref="~/army"
        icon="back"
        menu={
          !readOnly && (
            <ActionsMenu>
              <DialogTrigger>
                <UpdateArmyDialog army={army} />
                <MenuDialogDisclosure ref={editButtonRef}>
                  Edit
                </MenuDialogDisclosure>
              </DialogTrigger>

              <DialogTrigger>
                <ConfirmDialog
                  heading="Duplicate army?"
                  description={`A new army with identical units and keywords will be created.`}
                  submitLabel="Duplicate"
                  onSubmit={async () => {
                    const id = await db.cloneArmy(army.data, {
                      disambiguateName: true,
                    });
                    navigate(`~/army/a/${id}`);
                  }}
                />
                <MenuDialogDisclosure>Duplicate</MenuDialogDisclosure>
              </DialogTrigger>

              <DialogTrigger>
                <ShareArmyDialog army={army} />
                <MenuDialogDisclosure>Share</MenuDialogDisclosure>
              </DialogTrigger>

              <MenuLink href={`~/army/a/${army.data.id}/print`}>Print</MenuLink>

              {canUpdateRuleset && (
                <DialogTrigger>
                  <UpdateRulesetDialog army={army} />
                  <MenuDialogDisclosure>Update ruleset</MenuDialogDisclosure>
                </DialogTrigger>
              )}

              <MenuDivider />

              <DialogTrigger>
                <ConfirmDialog
                  heading="Delete army?"
                  description={`Are you sure you want to delete "${army.name}"?`}
                  submitLabel="Delete"
                  onSubmit={async () => {
                    await db.deleteArmy(army.data);
                    navigate("~/army");
                  }}
                />
                <MenuDialogDisclosure>Delete</MenuDialogDisclosure>
              </DialogTrigger>
            </ActionsMenu>
          )
        }
      >
        <div className={css.armyDetailView}>
          <div
            className={atomic({
              display: "flex",
              flexDirection: "column",
              gap: 24,
            })}
          >
            <div className={css.armyHeader}>
              <Heading className={css.armyName}>
                <span onDoubleClick={() => editButtonRef.current?.click()}>
                  {army.name}
                </span>
              </Heading>
              <p className={css.armySummary}>
                {army.summary}
                {canUpdateRuleset && (
                  <span>
                    {` · Ruleset v${army.ruleset.version} `}
                    <DialogTrigger>
                      <UpdateRulesetDialog army={army} />
                      <DialogDisclosure className={inlineDisclosure}>
                        (update)
                      </DialogDisclosure>
                    </DialogTrigger>
                  </span>
                )}
              </p>

              {!readOnly && (
                <div className={css.armyActions}>
                  <DialogTrigger>
                    <ShareArmyDialog army={army} />
                    <DialogDisclosure className={css.armyAction}>
                      Share
                    </DialogDisclosure>
                  </DialogTrigger>

                  <Link
                    href={`~/army/a/${army.data.id}/print`}
                    className={css.armyAction}
                  >
                    Print
                  </Link>
                </div>
              )}
            </div>

            {army.data.description && (
              <Paragraphs text={army.data.description} />
            )}

            {canUpdateRuleset && (
              <InfoBox heading="Outdated">
                This army is using ruleset v{army.ruleset.version}, but v
                {rulesets.latest.version} is the latest.{" "}
                <DialogTrigger>
                  <UpdateRulesetDialog army={army} />
                  <DialogDisclosure className={inlineDisclosure}>
                    Update to latest?
                  </DialogDisclosure>
                </DialogTrigger>
              </InfoBox>
            )}

            {army.failingConditions.length > 0 && (
              <ArmyConstraintsValidationBox army={army} />
            )}
          </div>

          {showArmyKeywordSection && (
            <HeadingLevel>
              <div>
                <SubtleHeading>Army keyword</SubtleHeading>

                <div>
                  {army.keyword ? (
                    <KeywordItem
                      name={army.keyword.name}
                      summary={army.keyword.summary}
                      standalone
                      icon={
                        !readOnly && (
                          <IconButton
                            label="Remove army keyword"
                            icon="cross"
                            onClick={() => {
                              void db.updateArmy({
                                ...army.data,
                                keywords: [],
                              });
                            }}
                          />
                        )
                      }
                    />
                  ) : !readOnly ? (
                    <DialogTrigger>
                      <ArmyKeywordsDialog {...props} />
                      <DialogListButton>Add keyword</DialogListButton>
                    </DialogTrigger>
                  ) : null}
                </div>
              </div>
            </HeadingLevel>
          )}

          <HeadingLevel>
            <div>
              <SubtleHeading>Units</SubtleHeading>
              <div>
                {army.units.length > 0 && (
                  <ul className={css.unitsList}>
                    {army.units.map((unit) => {
                      return <UnitListItem key={unit.data.id} unit={unit} />;
                    })}
                  </ul>
                )}

                {!readOnly ? (
                  <DialogTrigger>
                    <CreateUnitDialog {...props} />
                    <DialogListButton>Add unit</DialogListButton>
                  </DialogTrigger>
                ) : (
                  <ListEndMarker />
                )}
              </div>
            </div>
          </HeadingLevel>

          {readOnly && (
            <DialogTrigger>
              <ConfirmDialog
                heading="Copy shared army?"
                description={`${army.name} will be copied over to your armies.`}
                submitLabel="Copy over"
                onSubmit={async () => {
                  const id = await army.ruleset.db.cloneArmy(army.data);
                  navigate(`~/army/a/${id}`);
                }}
              />
              <DialogDisclosureButton>Copy shared army</DialogDisclosureButton>
            </DialogTrigger>
          )}
        </div>
      </ContentView>
    </>
  );
}

function ArmyView(props: { army: Army; readOnly?: boolean }) {
  const isWide = useMediaQuery(MinWidth.WIDE);
  useDocumentTheme(props.army.theme);

  if (!isWide) {
    return (
      <Switch>
        <Route path="/">
          <ArmyDetailView {...props} />
        </Route>
        <Route path="/u/:unitId">
          <UnitDetailView {...props} headerIcon="back" />
        </Route>
      </Switch>
    );
  }

  return (
    <HeadingLevel>
      <main className={css.view}>
        <div
          className={atomic({
            overflow: "auto",
            xScrollbarVisibility: "hidden",
          })}
        >
          <ArmyDetailView {...props} />
        </div>

        <div className={css.divider}>
          <VerticalDivider />
        </div>

        <div
          className={atomic({
            overflow: "auto",
            xScrollbarVisibility: "hidden",
          })}
        >
          <Switch>
            <Route path="/u/:unitId">
              <UnitDetailView {...props} headerIcon="cross" />
            </Route>

            <Route>
              <div className={css.illustrationWrap}>
                <div className={css.illustration} />
              </div>
            </Route>
          </Switch>
        </div>
      </main>
    </HeadingLevel>
  );
}

export function ArmyDetailPage() {
  const armyOp = useArmyQuery();

  if (armyOp.isPending) {
    return (
      <ContentView heading="Army" backHref="~/army" icon="back">
        <LoadingView />
      </ContentView>
    );
  }

  if (armyOp.isFailure) {
    return (
      <ContentView heading="Army" backHref="~/army" icon="back">
        <KnownErrorView code={armyOp.failure.type} />
      </ContentView>
    );
  }

  return <ArmyView army={armyOp.value} />;
}

export function SharedArmyDetailPage() {
  const rulesets = useRulesets();
  const { shareId } = useParams<{ shareId: string }>();
  const armyDataOp = useFetchSharedArmy(shareId);
  const armyOp = armyDataOp.mapSuccess((data) => rulesets.createArmy(data));

  if (armyOp.isPending) {
    return (
      <ContentView heading="Shared Army" backHref="~/army" icon="back">
        <LoadingView />
      </ContentView>
    );
  }

  if (armyOp.isFailure) {
    return (
      <ContentView heading="Shared Army" backHref="~/army" icon="back">
        <KnownErrorView code={armyOp.failure.type} />
      </ContentView>
    );
  }

  return <ArmyView army={armyOp.value} readOnly />;
}
